/**
 * RegionalItineraryPage — programmatic SEO landing page template.
 *
 * URL pattern: /wineries/:region/best-:intent-itineraries
 *   e.g. /wineries/napa/best-dog-friendly-itineraries
 *        /wineries/sonoma/best-romantic-itineraries
 *        /wineries/willamette/best-pinot-noir-itineraries
 *
 * Purpose: capture long-tail "best [intent] in [region]" search queries
 * AND satisfy modern AI Answer Engines (Perplexity, Gemini, ChatGPT
 * search). Renders 1,500-2,000 word rich content — TL;DR, quick-facts
 * table, "why picked" reasoning, cost breakdowns, insider tips,
 * common mistakes, best-time guidance, nearby dining, and FAQs.
 *
 * Data: GET /api/regional-guides/:region/:intentSlug (database, edited in
 * Admin > Itinerary Guides; the sitemap reads the same table). The shipped
 * /regional-guides.json is the fallback when the API is unreachable
 * (lib/guidesSource).
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, MapPin } from '@phosphor-icons/react';
import axios from 'axios';
import { getBackendUrl } from '@/lib/runtimeConfig';
import FAQSchema from '@/components/seo/FAQSchema';
import useJsonLd from '@/hooks/useJsonLd';
import { wineryContact } from '@/lib/wineryContact';
import InArticleAd from '@/components/ads/InArticleAd';
import MultiplexAd from '@/components/ads/MultiplexAd';
import { loadRegionalGuide } from '@/lib/guidesSource';

const API = `${getBackendUrl()}/api`;
const SITE_URL = 'https://www.wineryfinderapp.com';

const RegionalItineraryPage = () => {
  const { region, intentPath } = useParams();
  // intentPath comes in as "best-dog-friendly-itineraries" — extract the intent slug
  const intentSlug = (intentPath || '')
    .replace(/^best-/, '')
    .replace(/-itineraries$/, '');

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [source, setSource] = useState(null); // 'api' | 'fallback' (data-guide-source)
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(true);

  // Guide content: API (DB) first, shipped JSON as the fallback.
  useEffect(() => {
    let alive = true;
    setConfigLoading(true);
    loadRegionalGuide(region, intentSlug, { onSource: (s) => { if (alive) setSource(s); } })
      .then((guide) => { if (alive) setConfig(guide); })
      .catch(() => { if (alive) setConfig(null); })
      .finally(() => { if (alive) setConfigLoading(false); });
    return () => { alive = false; };
  }, [region, intentSlug]);

  useEffect(() => {
    if (!config) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/ai-search/trips/popular`, {
          params: { limit: 12 },
          timeout: 10000,
        });
        const kws = (config.filters?.keywords || []).map((k) => k.toLowerCase());
        const regionKw = (config.filters?.region || '').toLowerCase();
        const filtered = (res?.data?.trips || []).filter((t) => {
          const q = (t.query || '').toLowerCase();
          return q.includes(regionKw) && kws.some((k) => q.includes(k));
        });
        setTrips(filtered);
      } catch { /* graceful degradation */ }
      finally { setTripsLoading(false); }
    })();
  }, [config]);

  // ---- Schema.org JSON-LD payloads (must compute before early returns to
  //      satisfy React's rules of hooks — useJsonLd is called every render). ----
  const title = config
    ? (config.seoTitle || `Best ${config.intent} Itineraries in ${config.region} (2026)`)
    : '';
  const description = config
    ? (config.seoDescription
      || `${config.region}'s top ${config.intent.toLowerCase()} wine itineraries, AI-planned and community-voted. 12 curated 3-stop day trips with drive times, tasting fees, and reasoning.`)
    : '';
  const url = config
    ? `${SITE_URL}/wineries/${region}/best-${config.intentSlug}-itineraries`
    : '';
  const heroImage = config?.heroImage;
  const heroImageAlt = config?.heroImageAlt || (config ? `${config.region} vineyards` : '');
  const photoCredit = config?.photoCredit;

  // BreadcrumbList JSON-LD — surfaces in Google search results as clickable trail.
  const breadcrumbJson = config ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Winery Finder', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Wine Country Guides', item: `${SITE_URL}/wine-country-guides` },
      { '@type': 'ListItem', position: 3, name: config.region, item: `${SITE_URL}/wineries/${region}` },
      { '@type': 'ListItem', position: 4, name: `Best ${config.intent} Itineraries`, item: url },
    ],
  } : null;

  // ItemList JSON-LD — helps AI Overviews cite the ranked list directly.
  const itemListJson = trips.length > 0
    ? {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: trips.map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_URL}/trip/${t.slug}`,
        name: t.query,
      })),
    }
    : null;

  // Article JSON-LD — helps Google/Gemini identify this as authoritative editorial content.
  const articleJson = config ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: heroImage,
    datePublished: config.lastUpdated || '2026-07-22',
    dateModified: config.lastUpdated || '2026-07-22',
    author: { '@type': 'Organization', name: 'Winery Finder' },
    publisher: {
      '@type': 'Organization',
      name: 'Winery Finder',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo192.png` },
    },
    mainEntityOfPage: url,
  } : null;

  // Inject schema.org JSON-LD directly into <head>. react-helmet-async
  // does not reliably lift inline <script> JSX children in React 18, so
  // we use useEffect + document.head via useJsonLd. useJsonLd is null-safe.
  useJsonLd('breadcrumb', breadcrumbJson);
  useJsonLd('article', articleJson);
  useJsonLd('itemlist', itemListJson);

  if (configLoading) {
    return (
      <div className="max-w-2xl mx-auto py-24 px-6 text-center" data-testid="regional-guide-loading">
        <div className="animate-pulse text-gray-500">Loading guide…</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="max-w-2xl mx-auto py-24 px-6 text-center" data-testid="regional-guide-not-found">
        <h1 className="text-3xl font-bold">Not found</h1>
        <p className="mt-4 text-gray-600">This regional guide doesn&apos;t exist yet.</p>
        <Link to="/wine-country-guides" className="mt-6 inline-block text-wine-600 underline">
          Browse all regional guides →
        </Link>
      </div>
    );
  }

  const quickFactsRows = config.quickFacts
    ? Object.entries(config.quickFacts).map(([k, v]) => ({
      label: k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim(),
      value: v,
    }))
    : [];

  return (
    <div className="max-w-5xl mx-auto py-16 md:py-20 px-6" data-testid="regional-itinerary-page" data-guide-source={source || undefined}>
      <Helmet>
        <title>{`${title} | Winery Finder`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        {heroImage && <meta property="og:image" content={heroImage} />}
      </Helmet>
      <FAQSchema faqs={config.faqs || []} />

      <nav className="text-xs text-gray-500 mb-8" aria-label="Breadcrumb" data-testid="regional-guide-breadcrumbs">
        <Link to="/" className="hover:text-wine-600">Home</Link>
        <span className="mx-2">›</span>
        <Link to="/wine-country-guides" className="hover:text-wine-600">Wine Country Guides</Link>
        <span className="mx-2">›</span>
        <span className="text-gray-700">{config.region}</span>
        <span className="mx-2">›</span>
        <span className="text-gray-900 font-medium">Best {config.intent}</span>
      </nav>

      <motion.header
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        data-testid="regional-guide-header"
      >
        <div className="text-xs font-bold uppercase tracking-widest text-wine-600">
          {config.region} · {config.intent} guides
        </div>
        <h1 className="mt-3 text-4xl md:text-5xl font-serif tracking-tight text-gray-900">
          Best {config.intent} Itineraries in {config.region}
        </h1>
        {config.lastUpdated && (
          <p className="mt-3 text-xs text-gray-500" data-testid="regional-guide-last-updated">
            Updated {new Date(config.lastUpdated).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        )}
        <p className="mt-6 text-lg text-gray-700 max-w-3xl leading-relaxed">
          {config.heroIntro}
        </p>
      </motion.header>

      {heroImage && (
        <div className="mt-10 overflow-hidden rounded-2xl shadow-sm" data-testid="regional-guide-hero-image">
          <img
            src={heroImage}
            alt={heroImageAlt}
            loading="lazy"
            className="w-full h-64 md:h-96 object-cover"
          />
          {photoCredit && (
            <p className="text-xs text-gray-400 text-right mt-1 px-1">
              {photoCredit.url
                ? <a href={photoCredit.url} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">{photoCredit.text}</a>
                : photoCredit.text}
            </p>
          )}
        </div>
      )}

      {config.tldr && (
        <section
          className="mt-12 rounded-2xl border border-wine-100 bg-wine-50/40 p-6 md:p-8"
          data-testid="regional-guide-tldr"
        >
          <div className="text-xs font-bold uppercase tracking-widest text-wine-700 mb-3">
            TL;DR
          </div>
          <p className="text-base md:text-lg text-gray-800 leading-relaxed">
            {config.tldr}
          </p>
        </section>
      )}

      {quickFactsRows.length > 0 && (
        <section className="mt-12" data-testid="regional-guide-quick-facts">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Quick facts</h2>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <tbody>
                {quickFactsRows.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 w-1/3 align-top">
                      {row.label}
                    </th>
                    <td className="px-4 py-3 text-gray-800">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {config.whyItWorks && (
        <section className="mt-16" data-testid="regional-guide-why-it-works">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Why {config.intent.toLowerCase()} trips work here
          </h2>
          <p className="text-gray-700 leading-relaxed">{config.whyItWorks}</p>
        </section>
      )}

      {config.whatToExpect && (
        <section className="mt-12" data-testid="regional-guide-what-to-expect">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">What to expect</h2>
          <p className="text-gray-700 leading-relaxed">{config.whatToExpect}</p>
        </section>
      )}

      {Array.isArray(config.expertPicks) && config.expertPicks.length > 0 && (
        <section className="mt-16" data-testid="regional-guide-expert-picks">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Expert picks & why we chose them
          </h2>
          <div
            className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900"
            data-testid="regional-guide-pricing-disclaimer"
          >
            <span className="mt-0.5 text-amber-700 flex-shrink-0" aria-hidden>ⓘ</span>
            <span>
              Tasting fees and reservation policies change frequently.
              Prices below were verified{' '}
              {config.lastUpdated
                ? new Date(config.lastUpdated).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                : 'July 2026'}
              {' '}— <strong>tap &ldquo;Call winery&rdquo; on any card</strong> to confirm the current fee before you book.
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {config.expertPicks.map((p, i) => {
              const contact = wineryContact({ name: p.name, region: config.region, phone: p.phone });
              return (
                <div
                  key={p.name || i}
                  className="rounded-xl border border-gray-200 p-5 hover:border-wine-300 transition"
                  data-testid={`regional-guide-pick-${i}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 text-lg">{p.name}</div>
                      <p className="mt-2 text-sm text-gray-700 leading-relaxed">{p.why}</p>
                    </div>
                    <div className="flex md:flex-col md:items-end gap-3 md:gap-1 text-xs text-gray-600">
                      {p.priceRange && (
                        <div className="whitespace-nowrap">
                          <span className="text-gray-400">Tasting:</span>{' '}
                          <span className="font-medium text-gray-800">{p.priceRange}</span>
                        </div>
                      )}
                      {p.reservation && (
                        <div className="whitespace-nowrap">
                          <span className="text-gray-400">Reservation:</span>{' '}
                          <span className="font-medium text-gray-800">{p.reservation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
                    {contact.telHref ? (
                      <a
                        href={contact.telHref}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-wine-600 hover:bg-wine-700 text-white text-xs font-semibold transition"
                        data-testid={`regional-guide-pick-${i}-call`}
                      >
                        <Phone size={12} weight="fill" />
                        Call {contact.displayPhone}
                      </a>
                    ) : (
                      <a
                        href={contact.mapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-wine-600 hover:bg-wine-700 text-white text-xs font-semibold transition"
                        data-testid={`regional-guide-pick-${i}-call`}
                      >
                        <Phone size={12} weight="fill" />
                        Call winery
                      </a>
                    )}
                    <a
                      href={contact.mapsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 hover:border-wine-400 hover:bg-wine-50 text-gray-700 hover:text-wine-700 text-xs font-semibold transition"
                      data-testid={`regional-guide-pick-${i}-directions`}
                    >
                      <MapPin size={12} weight="fill" />
                      Hours & directions
                    </a>
                    <span className="text-[11px] text-gray-500 ml-1">
                      Confirms current tasting fee & reservation policy
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Between sections only (never inside the card grid). The pick
          cards end in Call / Directions buttons, so this unit keeps 4rem
          clear of them when filled. */}
      {Array.isArray(config.expertPicks) && config.expertPicks.length > 0 && (
        <InArticleAd position="itinerary-1" gap="4rem" />
      )}

      {Array.isArray(config.costBreakdown) && config.costBreakdown.length > 0 && (
        <section className="mt-16" data-testid="regional-guide-cost-breakdown">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Cost breakdown
          </h2>
          <p className="text-xs text-gray-500 mb-6" data-testid="regional-guide-cost-disclaimer">
            Ranges reflect typical{' '}
            {config.lastUpdated
              ? new Date(config.lastUpdated).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
              : 'July 2026'}
            {' '}pricing. Tasting fees and menus change often — call each winery to confirm before you book.
          </p>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Item</th>
                  <th className="text-right px-4 py-3 font-medium">Estimated cost</th>
                </tr>
              </thead>
              <tbody>
                {config.costBreakdown.map((row, i) => {
                  const isTotal = /total/i.test(row.item);
                  return (
                    <tr
                      key={row.item}
                      className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isTotal ? 'font-semibold text-gray-900' : 'text-gray-800'}`}
                    >
                      <td className="px-4 py-3">{row.item}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">{row.cost}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {Array.isArray(config.insiderTips) && config.insiderTips.length > 0 && (
        <section className="mt-16" data-testid="regional-guide-insider-tips">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Insider tips</h2>
          <ul className="space-y-3">
            {config.insiderTips.map((tip, i) => (
              <li key={i} className="flex gap-3 text-gray-700 leading-relaxed">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-wine-500 flex-shrink-0" aria-hidden />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {Array.isArray(config.insiderTips) && config.insiderTips.length > 0 && (
        <InArticleAd position="itinerary-2" gap="3rem" />
      )}

      {Array.isArray(config.commonMistakes) && config.commonMistakes.length > 0 && (
        <section className="mt-16" data-testid="regional-guide-common-mistakes">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Common mistakes to avoid</h2>
          <ul className="space-y-3">
            {config.commonMistakes.map((mistake, i) => (
              <li key={i} className="flex gap-3 text-gray-700 leading-relaxed">
                <span className="mt-1 text-red-500 font-semibold flex-shrink-0" aria-hidden>×</span>
                <span>{mistake}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {config.bestTimeToVisit && (
        <section className="mt-16" data-testid="regional-guide-best-time">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best time to visit</h2>
          <p className="text-gray-700 leading-relaxed">{config.bestTimeToVisit}</p>
        </section>
      )}

      {Array.isArray(config.nearbyDining) && config.nearbyDining.length > 0 && (
        <section className="mt-16" data-testid="regional-guide-nearby-dining">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Nearby dining</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.nearbyDining.map((d, i) => (
              <div
                key={d.name || i}
                className="rounded-xl border border-gray-200 p-4"
                data-testid={`regional-guide-dining-${i}`}
              >
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <div className="font-semibold text-gray-900">{d.name}</div>
                  {d.type && <div className="text-xs text-gray-500 whitespace-nowrap">{d.type}</div>}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{d.why}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {config.alternativesIfBusy && (
        <section className="mt-16" data-testid="regional-guide-alternatives">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            If your first choices are booked
          </h2>
          <p className="text-gray-700 leading-relaxed">{config.alternativesIfBusy}</p>
        </section>
      )}

      {/* One Multiplex unit at the end of the editorial body: after prose,
          before the community-trips heading. Not between the trip cards
          (clickable links / "Plan the first one" CTA) and the FAQ. */}
      <MultiplexAd />

      <section className="mt-20" data-testid="regional-guide-community-trips">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          Top community-voted itineraries
        </h2>
        {tripsLoading && <div className="text-sm text-gray-500">Loading trips…</div>}
        {!tripsLoading && trips.length === 0 && (
          <div className="text-sm text-gray-500 border rounded-xl p-6 bg-gray-50" data-testid="regional-guide-empty-state">
            No matching {config.intent.toLowerCase()} itineraries saved yet.{' '}
            <Link to="/concierge" className="text-wine-600 underline">
              Plan the first one →
            </Link>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {trips.map((t) => (
            <Link
              key={t.slug}
              to={`/trip/${t.slug}`}
              className="block p-5 rounded-xl border border-gray-200 hover:border-wine-400 hover:shadow-md transition"
              data-testid={`regional-trip-${t.slug}`}
            >
              <div className="text-xs text-gray-500 mb-2">{t.vote_count || 0} votes</div>
              <div className="font-semibold text-gray-900 mb-2">&ldquo;{t.query}&rdquo;</div>
              <div className="text-sm text-gray-600">{(t.stop_names || []).join(' · ')}</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-20" data-testid="regional-guide-faqs">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Frequently asked</h2>
        <div className="space-y-6">
          {(config.faqs || []).map((f, i) => (
            <div key={i} data-testid={`regional-guide-faq-${i}`}>
              <h3 className="text-lg font-medium text-gray-900">{f.q}</h3>
              <p className="mt-2 text-gray-700 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-20 pt-10 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Want to plan a {config.intent.toLowerCase()} trip in a different region? Try{' '}
          <Link to="/concierge" className="text-wine-600 underline" data-testid="regional-guide-concierge-cta">the AI concierge</Link>{' '}
          — it plans a 3-stop day in ~6 seconds. Or{' '}
          <Link to="/wine-country-guides" className="text-wine-600 underline" data-testid="regional-guide-index-cta">
            browse all regional guides
          </Link>.
        </p>
      </section>
    </div>
  );
};

export default RegionalItineraryPage;
